import { useEffect, useState } from "react";
import { Eye, Users, CalendarDays, Activity } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const Bar = ({ label, value, max }) => (
  <div>
    <div className="flex justify-between gap-4 text-xs tracking-wide text-[#7C7163]">
      <span className="truncate">{label}</span>
      <span className="shrink-0 text-[#262019]">{value}</span>
    </div>
    <div className="mt-1.5 h-1 w-full bg-[#EFE8DC]">
      <div
        className="h-1 bg-[#A58343] transition-[width] duration-700"
        style={{ width: `${max ? Math.max(4, Math.round((value / max) * 100)) : 0}%` }}
      />
    </div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="border border-[#A58343]/20 bg-[#FFFFFF] p-8" data-testid={testid}>
    <Icon size={20} strokeWidth={1.25} className="text-[#A58343]" />
    <p className="mt-4 font-serif text-5xl font-light text-[#262019]">{value}</p>
    <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#7C7163]">{label}</p>
  </div>
);

export default function AnalyticsDashboard() {
  const [key, setKey] = useState(localStorage.getItem("merakee_analytics_key") || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    try {
      const r = await api.get("/analytics/summary", { params: { key: k } });
      setData(r.data);
      setKey(k);
      localStorage.setItem("merakee_analytics_key", k);
    } catch {
      toast.error("Incorrect passcode.");
      localStorage.removeItem("merakee_analytics_key");
      setKey("");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 pt-20" data-testid="analytics-gate">
        <div className="w-full max-w-sm border border-[#A58343]/25 bg-[#FFFFFF] p-10 text-center">
          <p className="overline">Private</p>
          <h1 className="mt-4 font-serif text-4xl font-light text-[#262019]">Merakee Analytics</h1>
          <p className="mt-3 text-sm text-[#7C7163]">Enter the passcode to view visitor insights.</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              load(input);
            }}
            className="mt-8"
          >
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Passcode"
              data-testid="analytics-passcode-input"
              className="field text-center"
            />
            <button type="submit" disabled={loading} className="btn-gold-solid mt-8 w-full" data-testid="analytics-open-button">
              {loading ? "Opening…" : "Open Dashboard"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const max7 = Math.max(...data.last7.map((d) => d.views), 1);
  const maxDevice = Math.max(...Object.values(data.devices), 1);
  const maxCountry = Math.max(...Object.values(data.countries), 1);
  const maxPage = Math.max(...Object.values(data.top_pages), 1);

  return (
    <div className="mx-auto max-w-6xl px-6 pb-24 pt-40 lg:px-8" data-testid="analytics-dashboard">
      <Reveal>
        <p className="overline">Private · Only you can see this</p>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
          <h1 className="font-serif text-4xl font-light text-[#262019] sm:text-5xl">
            Visitor <span className="italic text-[#A58343]">Insights</span>
          </h1>
          <button
            onClick={() => load(key)}
            data-testid="analytics-refresh-button"
            className="btn-gold !px-6 !py-2.5"
          >
            Refresh
          </button>
        </div>
      </Reveal>

      <div className="mt-12 grid gap-6 sm:grid-cols-3">
        <StatCard icon={Eye} label="Total Page Views" value={data.total_views} testid="stat-total-views" />
        <StatCard icon={Users} label="Unique Visitors" value={data.unique_visitors} testid="stat-unique-visitors" />
        <StatCard icon={CalendarDays} label="Views Today" value={data.today_views} testid="stat-today-views" />
      </div>

      <Reveal delay={0.1}>
        <div className="mt-12 border border-[#A58343]/20 bg-[#FFFFFF] p-8" data-testid="analytics-last7">
          <p className="overline !text-[10px]">Last 7 Days</p>
          <div className="mt-8 flex h-36 items-end gap-3 sm:gap-6">
            {data.last7.map((d) => (
              <div key={d.date} className="flex flex-1 flex-col items-center gap-3">
                <span className="text-xs text-[#262019]">{d.views}</span>
                <div
                  className="w-full max-w-10 bg-[#A58343]/80 transition-[height] duration-700"
                  style={{ height: `${Math.max(4, Math.round((d.views / max7) * 100))}%` }}
                />
                <span className="text-[10px] uppercase tracking-widest text-[#7C7163]">{d.date}</span>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <div className="mt-12 grid gap-12 lg:grid-cols-2">
        <Reveal delay={0.15}>
          <div data-testid="analytics-devices">
            <p className="overline !text-[10px]">Devices</p>
            <div className="mt-6 space-y-5">
              {Object.entries(data.devices).map(([k, v]) => (
                <Bar key={k} label={k} value={v} max={maxDevice} />
              ))}
              {Object.keys(data.devices).length === 0 && (
                <p className="text-sm text-[#7C7163]">No visits yet — share the website link to start measuring.</p>
              )}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div data-testid="analytics-countries">
            <p className="overline !text-[10px]">Countries</p>
            <div className="mt-6 space-y-5">
              {Object.entries(data.countries).map(([k, v]) => (
                <Bar key={k} label={k} value={v} max={maxCountry} />
              ))}
              {Object.keys(data.countries).length === 0 && (
                <p className="text-sm text-[#7C7163]">Country data appears with your first visitors.</p>
              )}
            </div>
          </div>
        </Reveal>
      </div>

      <div className="mt-12 grid gap-12 lg:grid-cols-2">
        <Reveal delay={0.25}>
          <div data-testid="analytics-top-pages">
            <p className="overline !text-[10px]">Most Viewed Pages</p>
            <div className="mt-6 space-y-5">
              {Object.entries(data.top_pages).map(([k, v]) => (
                <Bar key={k} label={k === "/" ? "Homepage" : k} value={v} max={maxPage} />
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <div data-testid="analytics-recent">
            <p className="overline !text-[10px]">Recent Activity</p>
            <div className="mt-6 space-y-4">
              {data.recent.map((v) => (
                <div key={v.id} className="flex items-center gap-4 border-b border-[#A58343]/10 pb-4 text-xs">
                  <Activity size={14} strokeWidth={1.5} className="shrink-0 text-[#A58343]" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[#262019]">{v.path === "/" ? "Homepage" : v.path}</p>
                    <p className="mt-0.5 text-[#7C7163]">
                      {v.device} · {v.country}
                    </p>
                  </div>
                  <span className="shrink-0 text-[#7C7163]">
                    {new Date(v.ts).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              ))}
              {data.recent.length === 0 && <p className="text-sm text-[#7C7163]">No activity yet.</p>}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
